import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { CheckCircle2, TrendingUp, PieChart, Zap } from 'lucide-react';

interface ProfileDashboardPreviewProps {
  profileData: any;
}

const ProfileDashboardPreview = ({ profileData }: ProfileDashboardPreviewProps) => {
  // Generate a sample risk score based on risk assessment data
  const calculateRiskScore = () => {
    const riskData = profileData.riskAssessment || {};
    let score = 50; // Default moderate risk
    
    if (riskData.marketDropResponse === 'buy-more') score += 20;
    if (riskData.marketDropResponse === 'sell-all') score -= 20;
    if (riskData.investmentPriority === 'grow-longterm') score += 10;
    if (riskData.investmentPriority === 'preserve-capital') score -= 10;
    
    // Keep score within 10-90 range
    return Math.min(Math.max(score, 10), 90);
  };
  
  const riskScore = calculateRiskScore();
  
  // Generate a sample recommended asset allocation
  const recommendedAllocation = {
    equity: riskScore,
    debt: 90 - riskScore,
    alternative: 10
  };
  
  // Calculate completion percentage based on filled sections
  const calculateCompletionPercentage = () => {
    const sections = Object.keys(profileData);
    const filledSections = sections.filter(section => 
      profileData[section] && Object.keys(profileData[section]).length > 0 &&
      !profileData[section].skipStep
    );
    
    return Math.round((filledSections.length / sections.length) * 100);
  };
  
  const completionPercentage = calculateCompletionPercentage();
  
  return (
    <div>
      <h2 className="text-xl font-semibold mb-6">Your Personalized Wealth Dashboard Preview</h2>
      
      <p className="text-white/70 mb-8">
        Based on the information you've provided, here's a preview of your personalized wealth dashboard.
        After completing the setup, you'll have access to detailed recommendations and tools.
      </p>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        {/* Profile Completion */}
        <Card className="bg-black border-[#9E7C47]/30">
          <CardContent className="p-5">
            <div className="flex items-center gap-3 mb-3">
              <CheckCircle2 className="h-5 w-5 text-[#9E7C47]" />
              <h3 className="font-medium">Profile Completion</h3>
            </div>
            
            <div className="w-full bg-[#9E7C47]/10 rounded-full h-3 mb-2">
              <div 
                className="bg-[#9E7C47] h-3 rounded-full" 
                style={{ width: `${completionPercentage}%` }}
              ></div>
            </div>
            
            <div className="flex justify-between text-sm">
              <span className="text-white/70">Completed</span>
              <span className="text-[#9E7C47] font-medium">{completionPercentage}%</span>
            </div>
          </CardContent>
        </Card>
        
        {/* Risk Profile */}
        <Card className="bg-black border-[#9E7C47]/30">
          <CardContent className="p-5">
            <div className="flex items-center gap-3 mb-4">
              <TrendingUp className="h-5 w-5 text-[#9E7C47]" />
              <h3 className="font-medium">Your Risk Profile</h3>
            </div>
            
            <div className="flex items-center gap-3">
              <div 
                className="w-full bg-gradient-to-r from-green-500 via-yellow-500 to-red-500 h-2 rounded-full relative"
              >
                <div 
                  className="absolute h-4 w-4 rounded-full bg-white border-2 border-[#9E7C47] top-1/2 -translate-y-1/2"
                  style={{ left: `${riskScore}%` }}
                ></div>
              </div>
            </div>
            
            <div className="flex justify-between text-sm mt-2">
              <span className="text-green-500">Conservative</span>
              <span className="text-yellow-500">Balanced</span>
              <span className="text-red-500">Aggressive</span>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Recommended Asset Allocation */}
      <Card className="bg-black border-[#9E7C47]/30 mb-8">
        <CardContent className="p-5">
          <div className="flex items-center gap-3 mb-4">
            <PieChart className="h-5 w-5 text-[#9E7C47]" />
            <h3 className="font-medium">Recommended Asset Allocation</h3>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            <div className="p-4 rounded-md bg-[#9E7C47]/10 text-center">
              <div className="text-2xl font-bold text-[#9E7C47]">{recommendedAllocation.equity}%</div>
              <div className="text-sm text-white/70">Equity</div>
            </div>
            
            <div className="p-4 rounded-md bg-[#9E7C47]/10 text-center">
              <div className="text-2xl font-bold text-[#9E7C47]">{recommendedAllocation.debt}%</div>
              <div className="text-sm text-white/70">Debt</div>
            </div>
            
            <div className="p-4 rounded-md bg-[#9E7C47]/10 text-center sm:col-span-2 md:col-span-1">
              <div className="text-2xl font-bold text-[#9E7C47]">{recommendedAllocation.alternative}%</div>
              <div className="text-sm text-white/70">Alternative</div>
            </div>
          </div>
          
          <p className="text-sm text-white/70 mt-4">
            This allocation is based on your risk profile and financial goals. Your actual portfolio may vary depending on your specific circumstances.
          </p>
        </CardContent>
      </Card>
      
      {/* What's Next */}
      <Card className="bg-gradient-to-r from-[#9E7C47]/20 to-[#9E7C47]/5 border-[#9E7C47]/30">
        <CardContent className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <Zap className="h-5 w-5 text-[#9E7C47]" />
            <h3 className="font-medium">What's Next</h3>
          </div>
          
          <ul className="space-y-3">
            <li className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-[#9E7C47]/70" />
              <span className="text-sm">Complete your investor profile setup</span>
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-[#9E7C47]/70" />
              <span className="text-sm">Review your personalized investment recommendations</span>
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-[#9E7C47]/70" />
              <span className="text-sm">Set up a regular investment plan (SIP)</span>
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-[#9E7C47]/70" />
              <span className="text-sm">Track your portfolio performance and rebalance as needed</span>
            </li>
          </ul>
          
          <p className="text-sm text-white/70 mt-6">
            After completing setup, you'll have access to personalized recommendations, investment tracking, and tools to help you achieve your financial goals.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProfileDashboardPreview;
